// Vulnerability data, nuclei, RetireJS API
import { api } from './client';

// Enhanced vulnerability data types
export interface CVEImportConfig {
  max_results: number;
  start_date?: string;
  end_date?: string;
  include_expired?: boolean;
  min_cvss?: number;
  sources?: string;
}

export interface BulkDeleteFilters {
  sources?: string[];
  severities?: string[];
  min_age?: number;
  max_age?: number;
  min_cvss?: number;
  max_cvss?: number;
  is_active?: boolean;
  is_custom?: boolean;
  has_exploit_db?: boolean;
  has_cve?: boolean;
  search_pattern?: string;
}

export interface BulkDeleteRequest {
  filters: BulkDeleteFilters;
  dry_run: boolean;
}

export interface BulkDeleteResponse {
  dry_run: boolean;
  total_matched: number;
  total_deleted: number;
  deleted_by_type: Record<string, number>;
  preview_rules?: any[];
  errors?: string[];
}

export interface FileImportConfig {
  format: 'json' | 'xml' | 'csv';
  source: string;
  overwrite_mode: 'skip' | 'update';
  validate_only: boolean;
}

export interface FileImportResponse {
  validated_items: number;
  imported_items: number;
  skipped_items: number;
  error_items: number;
  errors?: string[];
  preview_rules?: any[];
}

export interface ImportPreset {
  id: number;
  name: string;
  description: string;
  config: CVEImportConfig;
  is_default: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface ImportLimits {
  max_cve_per_import: number;
  max_daily_imports: number;
  max_file_size_mb: number;
  allowed_file_formats: string[];
  rate_limit_delay_seconds: number;
  auto_cleanup_days: number;
}

// Health check
export const healthAPI = {
  check: () => {
    console.log('🏥 Checking API health');
    return api.get('/health');
  },
};

// ENHANCED: VulnData API with new functionality
export const vulnDataAPI = {
  // Existing methods...
  getStats: () => {
    console.log('📊 Fetching vulnerability data statistics');
    return api.get('/vulndata/stats');
  },

  getRules: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('📋 Fetching vulnerability rules', queryString);
    return api.get(`/vulndata/rules${queryString}`);
  },

  getRule: (id: number) => {
    console.log('📋 Fetching vulnerability rule:', id);
    return api.get(`/vulndata/rules/${id}`);
  },

  createRule: (data: any) => {
    console.log('➕ Creating custom vulnerability rule:', data.name);
    return api.post('/vulndata/rules', data);
  },

  updateRule: (id: number, data: any) => {
    console.log('📝 Updating vulnerability rule:', id, data);
    return api.put(`/vulndata/rules/${id}`, data);
  },

  deleteRule: (id: number) => {
    console.log('🗑️ Deleting vulnerability rule:', id);
    return api.delete(`/vulndata/rules/${id}`);
  },

  // Bulk Operations
  bulkDeleteRules: (request: BulkDeleteRequest) => {
    console.log('🗑️ Bulk deleting vulnerability rules:', request);
    return api.post<BulkDeleteResponse>('/vulndata/rules/bulk-delete', request);
  },

  previewBulkDelete: (filters: BulkDeleteFilters) => {
    console.log('👁️ Previewing bulk delete:', filters);
    return api.post<BulkDeleteResponse>('/vulndata/rules/bulk-delete', { filters, dry_run: true });
  },

  bulkUpdateRules: (filters: BulkDeleteFilters, updates: any) => {
    console.log('📝 Bulk updating vulnerability rules:', filters, updates);
    return api.post('/vulndata/rules/bulk-update', { filters, updates });
  },

  bulkExportRules: (filters: BulkDeleteFilters, format: 'json' | 'csv' | 'xml') => {
    console.log('📤 Bulk exporting vulnerability rules:', filters, format);
    return api.post('/vulndata/rules/bulk-export', { filters, format }, { responseType: 'blob' });
  },

  // Database methods
  getDatabases: () => {
    console.log('🗄️ Fetching vulnerability databases');
    return api.get('/vulndata/databases');
  },

  updateDatabase: (id: number, data: any) => {
    console.log('📝 Updating vulnerability database:', id, data);
    return api.put(`/vulndata/databases/${id}`, data);
  },

  // Import methods
  getImports: () => {
    console.log('📥 Fetching import history');
    return api.get('/vulndata/imports');
  },

  startImport: (databaseId: number) => {
    console.log('▶️ Starting import for database:', databaseId);
    return api.post(`/vulndata/databases/${databaseId}/import`);
  },

  // Enhanced Import with Configuration
  startEnhancedImport: (databaseId: number, config: CVEImportConfig) => {
    console.log('🚀 Starting enhanced import for database:', databaseId, 'with config:', config);
    return api.post(`/vulndata/databases/${databaseId}/import-enhanced`, config);
  },

  // File-based Import
  importFromFile: (databaseId: number, file: File, config: FileImportConfig) => {
    console.log('📁 Importing from file for database:', databaseId, 'file:', file.name, 'config:', config);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('config', JSON.stringify(config));

    return api.post<FileImportResponse>(`/vulndata/databases/${databaseId}/import-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },

  validateFile: (databaseId: number, file: File, format: 'json' | 'xml' | 'csv') => {
    console.log('✅ Validating file for database:', databaseId, 'file:', file.name);

    const config: FileImportConfig = {
      format,
      source: 'file',
      overwrite_mode: 'skip',
      validate_only: true
    };

    return vulnDataAPI.importFromFile(databaseId, file, config);
  },

  // Import Configuration Management
  getImportConfig: (databaseId: number) => {
    console.log('⚙️ Fetching import configuration for database:', databaseId);
    return api.get(`/vulndata/databases/${databaseId}/import-config`);
  },

  updateImportConfig: (databaseId: number, config: CVEImportConfig) => {
    console.log('💾 Updating import configuration for database:', databaseId, config);
    return api.put(`/vulndata/databases/${databaseId}/import-config`, config);
  },

  // Import Presets
  getImportPresets: () => {
    console.log('📋 Fetching import presets');
    return api.get<ImportPreset[]>('/vulndata/import-presets');
  },

  createImportPreset: (preset: Omit<ImportPreset, 'id' | 'created_at' | 'updated_at'>) => {
    console.log('➕ Creating import preset:', preset.name);
    return api.post<ImportPreset>('/vulndata/import-presets', preset);
  },

  updateImportPreset: (id: number, preset: Partial<ImportPreset>) => {
    console.log('📝 Updating import preset:', id);
    return api.put<ImportPreset>(`/vulndata/import-presets/${id}`, preset);
  },

  deleteImportPreset: (id: number) => {
    console.log('🗑️ Deleting import preset:', id);
    return api.delete(`/vulndata/import-presets/${id}`);
  },

  getImportStatus: (importId: number) => {
    console.log('📊 Fetching import status:', importId);
    return api.get(`/vulndata/imports/${importId}/status`);
  },

  // Config methods
  getConfig: () => {
    console.log('⚙️ Fetching vulnerability configuration');
    return api.get('/vulndata/config');
  },

  updateConfig: (data: any) => {
    console.log('💾 Updating vulnerability configuration:', data);
    return api.put('/vulndata/config', data);
  },

  // Import Limits Configuration
  getImportLimits: () => {
    console.log('📏 Fetching import limits configuration');
    return api.get<ImportLimits>('/vulndata/config/import-limits');
  },

  updateImportLimits: (limits: Partial<ImportLimits>) => {
    console.log('📏 Updating import limits configuration:', limits);
    return api.put<ImportLimits>('/vulndata/config/import-limits', limits);
  },

  // Maintenance Operations (Admin only)
  cleanupOldData: (days: number) => {
    console.log('🧹 Cleaning up old vulnerability data older than', days, 'days');
    return api.post('/admin/vulndata/maintenance/cleanup', { days });
  },

  rebuildSearchIndex: () => {
    console.log('🔄 Rebuilding vulnerability search index');
    return api.post('/admin/vulndata/maintenance/rebuild-index');
  },

  getMaintenanceStats: () => {
    console.log('📊 Fetching maintenance statistics');
    return api.get('/admin/vulndata/maintenance/stats');
  },

  // Filtering Statistics
  getFilteringStats: () => {
    console.log('📊 Fetching vulnerability data filtering statistics');
    return api.get('/vulndata/filter/stats');
  },

  // CVE Sync API Methods
  getSyncStatus: () => {
    console.log('🔄 Fetching CVE sync status');
    return api.get('/vulndata/sync/status');
  },

  getSyncPresets: () => {
    console.log('📋 Fetching CVE sync presets');
    return api.get('/vulndata/sync/presets');
  },

  syncRecent: (days: number) => {
    console.log('🔄 Starting recent CVE sync for', days, 'days');
    return api.post('/vulndata/sync/recent', { days });
  },

  syncCritical: (days: number) => {
    console.log('🔄 Starting critical CVE sync for', days, 'days');
    return api.post('/vulndata/sync/critical', { days });
  },

  syncByKeyword: (keyword: string, days: number) => {
    console.log('🔄 Starting keyword CVE sync:', keyword, 'for', days, 'days');
    return api.post('/vulndata/sync/keyword', { keyword, days });
  },

  syncKEV: () => {
    console.log('🔄 Starting KEV (Known Exploited Vulnerabilities) sync');
    return api.post('/vulndata/sync/kev');
  },

  fullBackfill: () => {
    console.log('🔄 Starting FULL historical NVD backfill (all CVEs)');
    return api.post('/vulndata/sync/full-backfill');
  },

  advancedSync: (options: Record<string, any>) => {
    console.log('🔄 Starting advanced CVE sync with options:', options);
    return api.post('/vulndata/sync/advanced', options);
  },

  quickSync: () => {
    console.log('🔄 Starting quick CVE sync');
    return api.post('/vulndata/sync/quick');
  },

  getRecommendedKeywords: () => {
    console.log('📋 Fetching recommended sync keywords');
    return api.get('/vulndata/sync/recommended-keywords');
  },

  updateEPSS: () => {
    console.log('🔄 Updating EPSS scores');
    return api.post('/vulndata/epss/update');
  },

  getHighRiskCVEs: (minEpss: number = 0.1) => {
    console.log('📋 Fetching high-risk CVEs with min EPSS:', minEpss);
    return api.get(`/vulndata/epss/high-risk?min_epss=${minEpss}`);
  },

  getPrioritizedVulnerabilities: () => {
    console.log('📋 Fetching prioritized vulnerabilities');
    return api.get('/vulndata/prioritized');
  },
};

// Helper utilities for bulk operations
export const bulkOperationUtils = {
  // Build filters from form data
  buildFilters: (formData: Record<string, any>): BulkDeleteFilters => {
    const filters: BulkDeleteFilters = {};

    if (formData.sources && formData.sources.length > 0) {
      filters.sources = formData.sources;
    }

    if (formData.severities && formData.severities.length > 0) {
      filters.severities = formData.severities;
    }

    if (formData.min_age && formData.min_age > 0) {
      filters.min_age = parseInt(formData.min_age);
    }

    if (formData.max_age && formData.max_age > 0) {
      filters.max_age = parseInt(formData.max_age);
    }

    if (formData.min_cvss && formData.min_cvss > 0) {
      filters.min_cvss = parseFloat(formData.min_cvss);
    }

    if (formData.max_cvss && formData.max_cvss > 0) {
      filters.max_cvss = parseFloat(formData.max_cvss);
    }

    if (formData.is_active !== undefined) {
      filters.is_active = formData.is_active;
    }

    if (formData.is_custom !== undefined) {
      filters.is_custom = formData.is_custom;
    }

    if (formData.has_exploit_db !== undefined) {
      filters.has_exploit_db = formData.has_exploit_db;
    }

    if (formData.has_cve !== undefined) {
      filters.has_cve = formData.has_cve;
    }

    if (formData.search_pattern && formData.search_pattern.trim()) {
      filters.search_pattern = formData.search_pattern.trim();
    }

    return filters;
  },

  // Validate file before upload
  validateFileForImport: (file: File): { valid: boolean; error?: string } => {
    const maxSize = 100 * 1024 * 1024; // 100MB
    const allowedExtensions = ['.json', '.xml', '.csv'];

    if (file.size > maxSize) {
      return { valid: false, error: 'File size exceeds 100MB limit' };
    }

    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(extension)) {
      return { valid: false, error: 'Unsupported file format. Use .json, .xml, or .csv files' };
    }

    return { valid: true };
  },

  // Format import configuration for display
  formatImportConfig: (config: CVEImportConfig): string => {
    const parts = [];

    if (config.max_results) {
      parts.push(`Max: ${config.max_results.toLocaleString()} CVEs`);
    }

    if (config.start_date || config.end_date) {
      const start = config.start_date || 'Beginning';
      const end = config.end_date || 'Now';
      parts.push(`Date: ${start} to ${end}`);
    }

    if (config.min_cvss && config.min_cvss > 0) {
      parts.push(`Min CVSS: ${config.min_cvss}`);
    }

    if (config.sources) {
      parts.push(`Sources: ${config.sources}`);
    }

    return parts.join(' \u2022 ') || 'Default configuration';
  },

  // Calculate estimated import time
  estimateImportTime: (config: CVEImportConfig): string => {
    const baseTimePerCVE = 0.1;
    const maxCVEs = config.max_results || 100;
    const estimatedSeconds = maxCVEs * baseTimePerCVE;

    if (estimatedSeconds < 60) {
      return `~${Math.ceil(estimatedSeconds)} seconds`;
    } else if (estimatedSeconds < 3600) {
      return `~${Math.ceil(estimatedSeconds / 60)} minutes`;
    } else {
      return `~${Math.ceil(estimatedSeconds / 3600)} hours`;
    }
  }
};

// Nuclei Template API
export const nucleiTemplateAPI = {
  getTemplates: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('📋 Fetching Nuclei templates', queryString);
    return api.get(`/nuclei/templates${queryString}`);
  },
  getTemplate: (id: number) => {
    console.log('📋 Fetching Nuclei template:', id);
    return api.get(`/nuclei/templates/${id}`);
  },
  createTemplate: (data: any) => {
    console.log('➕ Creating Nuclei template:', data.name);
    return api.post('/nuclei/templates', data);
  },
  updateTemplate: (id: number, data: any) => {
    console.log('📝 Updating Nuclei template:', id);
    return api.put(`/nuclei/templates/${id}`, data);
  },
  deleteTemplate: (id: number) => {
    console.log('🗑️ Deleting Nuclei template:', id);
    return api.delete(`/nuclei/templates/${id}`);
  },
  getTemplateStats: () => {
    console.log('📊 Fetching Nuclei template statistics');
    return api.get('/nuclei/templates/stats');
  },
  getTemplateCategories: () => {
    console.log('📂 Fetching Nuclei template categories');
    return api.get('/nuclei/templates/categories');
  },
  downloadTemplates: (data: any) => {
    console.log('⬇️ Starting Nuclei template download:', data);
    return api.post('/nuclei/templates/download', data);
  },
  getDownloadStatus: (id: number) => {
    console.log('📊 Fetching download status:', id);
    return api.get(`/nuclei/templates/download/${id}/status`);
  },
  getDownloads: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('📥 Fetching template downloads', queryString);
    return api.get(`/nuclei/templates/downloads${queryString}`);
  },
  importTemplates: (data: any) => {
    console.log('📤 Importing Nuclei templates:', data);
    return api.post('/nuclei/templates/import', data);
  },
  exportTemplates: (filters: any, format: string) => {
    console.log('📤 Exporting Nuclei templates:', filters, format);
    return api.post('/nuclei/templates/export', { filters, format }, { responseType: 'blob' });
  },
  getTemplateConfig: () => {
    console.log('⚙️ Fetching Nuclei template configuration');
    return api.get('/nuclei/templates/config');
  },
  updateTemplateConfig: (data: any) => {
    console.log('💾 Updating Nuclei template configuration:', data);
    return api.put('/nuclei/templates/config', data);
  },
  reindexTemplates: (templatesDir?: string) => {
    console.log('🔄 Starting Nuclei template reindex');
    return api.post('/nuclei/templates/reindex', templatesDir ? { templates_dir: templatesDir } : {});
  },
  getSyncStatus: () => {
    console.log('🔄 Fetching template sync status');
    return api.get('/nuclei/templates/sync/status');
  },
  startSync: (data: any) => {
    console.log('🔄 Starting template synchronization:', data);
    return api.post('/nuclei/templates/sync/start', data);
  },
  updateSyncConfig: (data: any) => {
    console.log('⚙️ Updating sync configuration:', data);
    return api.put('/nuclei/templates/sync/config', data);
  },
};

// RetireJS Security Analysis API
export const retireJSAPI = {
  getStats: () => {
    console.log('📊 Fetching RetireJS vulnerability statistics');
    return api.get('/retirejs/stats');
  },
  getVulnerabilities: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('🔍 Fetching RetireJS vulnerabilities', queryString);
    return api.get(`/retirejs/vulnerabilities${queryString}`);
  },
  getVulnerability: (id: string) => {
    console.log('🔍 Fetching RetireJS vulnerability details:', id);
    return api.get(`/retirejs/vulnerabilities/${id}`);
  },
  getLibraries: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('📚 Fetching JavaScript libraries', queryString);
    return api.get(`/retirejs/libraries${queryString}`);
  },
  getLibrary: (id: string) => {
    console.log('📚 Fetching JavaScript library details:', id);
    return api.get(`/retirejs/libraries/${id}`);
  },
  getScanResults: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('🔍 Fetching RetireJS scan results', queryString);
    return api.get(`/retirejs/scan-results${queryString}`);
  },
  getConfig: () => {
    console.log('⚙️ Fetching RetireJS configuration');
    return api.get('/retirejs/config');
  },
  updateConfig: (config: any) => {
    console.log('💾 Updating RetireJS configuration:', config);
    return api.put('/retirejs/config', config);
  },
  updateDatabase: () => {
    console.log('🔄 Updating RetireJS vulnerability database');
    return api.post('/retirejs/update-database');
  },
  getDatabaseStatus: () => {
    console.log('📊 Fetching RetireJS database status');
    return api.get('/retirejs/database-status');
  },
};
